import { DeleteOutline } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as countries from "country-list";
import ISO6391 from "iso-639-1";
import React, { useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Unauthorized } from "../../components";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { CustomTabs } from "../../components/CustomTabs";
import { LabeledTextField } from "../../components/LabeledTextField";
import { LabelValue } from "../../components/LabelValue";
import { PageLayout } from "../../components/PageLayout";
import { useCurrentUser } from "../../hooks";
import { USER_GROUPS_QUERY_KEY } from "../../hooks/useAllUserGroupsForACompany";
import { useCheckAreaAccess } from "../../hooks/useCheckAreaAccess";
import { useCompanyData } from "../../hooks/useCompanyData";
import { LICENSE_GROUP_ALLOCATIONS_QUERY_KEY } from "../../hooks/useExternalExperiences";
import { useLocalization } from "../../hooks/useLocalization";
import { useTabNavigation } from "../../hooks/useTabNavigation";
import { formatDateYMD } from "../../shared/utils/date";
import { EUserTabs, Routes } from "../../types/NavigationPaths";
import { USERS_FOR_RELEASE_CHANNEL_QUERY_KEY } from "../experiences/hooks/useUsersForReleaseChannel";
import { USERS_IN_GROUP_QUERY_KEY } from "../groups/hooks/useUsersInGroup";
import { ExperiencesTab } from "./ExperiencesTab/ExperiencesTab";
import { GroupsTab } from "./GroupsTab/GroupsTab";
import { useUserExperiences } from "./hooks/useUserExperiences";
import { useUserExternalExperiences } from "./hooks/useUserExternalExperiences";
import { useUserGroups } from "./hooks/useUserGroups";
import { USERS_QUERY_KEY } from "./hooks/useUsers";
import { RolesTab } from "./RolesTab/RolesTab";

export const USER_QUERY_KEY = "user";

const formatLanguage = (languageCode?: string, t?: (key: string) => string) => {
  if (!languageCode) return t ? t("users.unknown") : "Unknown";

  const languageName = ISO6391.getName(languageCode);

  return languageName || languageCode;
};

const formatCountry = (countryCode?: string, t?: (key: string) => string) => {
  if (!countryCode) return t ? t("users.unknown") : "Unknown";

  const countryName = countries.getName(countryCode.toUpperCase());

  return countryName ?? (t ? t("users.unknown") : "Unknown");
};

const formatLastLogin = (lastLogin?: Date, t?: (key: string) => string) => {
  if (!lastLogin) return t ? t("users.unknown") : "Unknown";

  return formatDateYMD(lastLogin);
};

export const UserDetailsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const userManagementClient = useUserManagementServiceV1();
  const { t } = useLocalization();
  const { companyId } = useParams();
  const { user: currentUser } = useCurrentUser();
  const { isLoading: isCheckAccessLoading, access } = useCheckAreaAccess(companyId);
  const areHooksEnabled = !!companyId && !isCheckAccessLoading && access.uam;

  const [successField, setSuccessField] = useState<"site" | null>(null);
  const [showResetPasswordSuccess, setShowResetPasswordSuccess] = useState(false);

  const tabLabels = useMemo(
    () => ({
      [EUserTabs.PROFILE]: t("users.tabs.profile"),
      [EUserTabs.ROLES]: t("users.tabs.roles"),
      [EUserTabs.GROUPS]: t("users.tabs.groups"),
      [EUserTabs.EXPERIENCES]: t("users.tabs.experiences"),
    }),
    [t],
  );

  const { activeTab, handleTabChange, tabItems } = useTabNavigation<EUserTabs>({
    tabEnum: EUserTabs,
    defaultTab: EUserTabs.PROFILE,
    labels: tabLabels,
  });

  const { data: user, isLoading: isUserLoading } = useQuery({
    queryKey: [USER_QUERY_KEY, userId],
    queryFn: () => userManagementClient.getUserById(userId!, false),
    enabled: !!userId,
  });

  // delete user logic
  const deleteUserMutation = useMutation({
    mutationFn: (targetUserId: string) =>
      userManagementClient.deleteUser(targetUserId),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: [USER_GROUPS_QUERY_KEY, userId] });
      void queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [USER_GROUPS_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [USERS_IN_GROUP_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [USERS_FOR_RELEASE_CHANNEL_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [LICENSE_GROUP_ALLOCATIONS_QUERY_KEY],
      });
      setIsDeleteDialogOpen(false);
      void navigate(`/companies/${companyId}${Routes.USERS}`);
    },
  });

  // Fetch user groups for the current user
  const { userGroups } = useUserGroups({
    userId: userId ?? "",
    enabled: areHooksEnabled && !!userId && !deleteUserMutation.isSuccess,
  });

  const { company, demoEmailDomain } = useCompanyData(companyId, areHooksEnabled);

  const { userExperiences } = useUserExperiences({
    userId: userId ?? "",
    enabled: areHooksEnabled && !!userId,
    userGroups,
  });

  const { userExternalExperiences } = useUserExternalExperiences({
    userId: userId ?? "",
    enabled: areHooksEnabled && !!userId,
    userGroups,
  });

  const combinedExperiences = useMemo(() => {
    return [...userExperiences, ...userExternalExperiences];
  }, [userExperiences, userExternalExperiences]);

  const isSsoUser = useMemo(() => {
    return user?.emailAddress &&
      company?.sSODomain &&
      user.emailAddress.split("@")[1]
      ? company.sSODomain.includes(user.emailAddress.split("@")[1] ?? "")
      : false;
  }, [company, user?.emailAddress]);

  const isDemoUser = useMemo(() => {
    if (!user?.emailAddress || !demoEmailDomain) return false;

    return user.emailAddress.endsWith(demoEmailDomain);
  }, [user?.emailAddress, demoEmailDomain]);

  const isViewingOwnProfile = useMemo(() => {
    return currentUser?.userId === user?.userId;
  }, [currentUser?.userId, user?.userId]);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // reset password logic
  const resetPasswordMutation = useMutation({
    mutationFn: (request: UserManagement_V1.ResetPasswordRequest) =>
      userManagementClient.requestPasswordReset(request),
    onSuccess: () => {
      setShowResetPasswordSuccess(true);
      setTimeout(() => setShowResetPasswordSuccess(false), 3000);
    },
  });

  const handlePasswordReset = () => {
    if (user?.emailAddress) {
      resetPasswordMutation.mutate(
        new UserManagement_V1.ResetPasswordRequest({
          userIdentifier: user.emailAddress,
          isV2: true,
        }),
      );
    }
  };

  const updateUserMutation = useMutation({
    mutationFn: (updateData: {
      userId: string;
      userData: UserManagement_V1.FullUserForUpdate;
      field: "site";
    }) =>
      userManagementClient.updateUserById(
        updateData.userId,
        updateData.userData,
      ),
    onSuccess: (_data, variables) => {
      setSuccessField(variables.field);
      setTimeout(() => setSuccessField(null), 3000);
      void queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [USER_QUERY_KEY, userId],
      });
    },
  });


  const handleSiteSave = (site: string) => {
    if (!user?.userId || site === (user.site ?? "")) {
      return;
    }

    updateUserMutation.mutate({
      userId: user.userId,
      userData: new UserManagement_V1.FullUserForUpdate({ ...user, site }),
      field: "site",
    });
  };

  const handleDeleteUser = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (user?.userId) {
      deleteUserMutation.mutate(user.userId);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    deleteUserMutation.reset();
  };

  const renderPageContent = () => {
    if (!isCheckAccessLoading && !access.uam) {
      return (<Unauthorized />)
    }

    if (!user) {
      return (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              {t("users.userNotFound", { userId })}
            </Typography>
          </CardContent>
        </Card>
      );
    }

    if (companyId != user.companyId) {
      void navigate("/");
    }

    return (
      <>
        <Stack spacing={{ xs: 1, md: 2 }}>
          <Box
            sx={{ height: 48, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 18, md: 24 },
                color: theme.colors.text[5],
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
              {user.surname}, {user.forename}
            </Typography>
          </Box>

          <CustomTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            tabs={tabItems} />
          <Box sx={{ paddingTop: { xs: 2, md: 0 } }}>
            {activeTab === EUserTabs.PROFILE && (
              <Stack
                spacing={{ xs: "24px", md: "8px" }}
                sx={{ paddingBottom: { xs: "80px", md: "32px" } }}>
                <LabelValue label={t("users.email")} value={user.emailAddress || t("users.notProvided")} />
                <LabelValue label={t("users.jobTitle")} value={user.jobTitle} />
                {isSsoUser ? (
                  <LabelValue label={t("users.site")} value={user.site} />
                ) : (
                  <LabelValue
                    boxSx={{ width: "100%", maxWidth: 232 }}
                    containerSx={{ height: 64 }}
                    label={t("users.site")}
                    showSuccessIndicator={successField === "site"}
                    successMessage={t("users.siteUpdated")}>
                    <LabeledTextField
                      defaultValue={user.site ?? ""}
                      disabled={updateUserMutation.isPending}
                      key={user.site}
                      maxLength={255}
                      onBlur={(event) => handleSiteSave(event.target.value)}
                      placeholder={t("users.sitePlaceholder")}
                      sx={{ mb: 0 }} />
                  </LabelValue>
                )}
                <LabelValue label={t("users.country")} value={formatCountry(user.country, t)} />
                <LabelValue label={t("users.language")} value={formatLanguage(user.language, t)} />
                <LabelValue label={t("users.lastLogin")} value={formatLastLogin(user.lastLogin, t)} />
                {
                  !isSsoUser && !isDemoUser && (
                    <LabelValue
                      containerSx={{ width: "fit-content" }}
                      label={t("users.password")}
                      showSuccessIndicator={showResetPasswordSuccess}
                      successMessage={t("users.resetEmailSent")}>
                      <Button
                        disabled={resetPasswordMutation.isPending}
                        onClick={handlePasswordReset}
                        size="small"
                        sx={{
                          padding: 0,
                          color: theme.colors.primary[5],
                          fontWeight: 600,
                          fontSize: 14,
                          "&:hover": {
                            textDecoration: "underline",
                            backgroundColor: "transparent",
                          },
                        }}
                        variant="text">
                        {t("users.resetViaEmail")}
                      </Button>
                    </LabelValue>
                  )
                }
                <LabelValue
                  containerSx={{ pt: "48px", height: "88px" }}
                  label={t("users.deleteUser")}>
                  <Button
                    disabled={
                      deleteUserMutation.isPending || isViewingOwnProfile
                    }
                    onClick={handleDeleteUser}
                    variant="secondary">
                    <DeleteOutline sx={{ fontSize: 24, mr: 1 }} />
                    {t("users.delete")}
                  </Button>
                </LabelValue>
              </Stack>
            )}
            {
              activeTab === EUserTabs.ROLES && (<RolesTab userId={userId ?? ""} />)
            }
            {
              activeTab === EUserTabs.GROUPS && (<GroupsTab user={user} userGroups={userGroups} />)
            }
            {
              activeTab === EUserTabs.EXPERIENCES && (<ExperiencesTab experiences={combinedExperiences} userId={userId ?? ""} />)
            }

          </Box>
        </Stack>

        {/* Delete User Confirmation Dialog */}
        <ConfirmationDialog.Root
          aria-describedby="delete-dialog-description"
          aria-labelledby="delete-dialog-title"
          maxWidth="xs"
          onClose={handleDeleteCancel}
          open={isDeleteDialogOpen}>
          <ConfirmationDialog.Title
            iconColor={theme.colors.orange}
            id="delete-dialog-title"
            showIcon={true}>
            {t("users.deleteUser")}
          </ConfirmationDialog.Title>
          <ConfirmationDialog.Content error={deleteUserMutation.error}>
            <ConfirmationDialog.Text id="delete-dialog-description">
              <Trans
                components={{ strong: <strong /> }}
                i18nKey="users.deleteConfirmation"
                values={{
                  userName: `${user.surname}, ${user.forename}`,
                }} />
            </ConfirmationDialog.Text>
          </ConfirmationDialog.Content>
          <ConfirmationDialog.Actions>
            <ConfirmationDialog.Button
              onClick={handleDeleteCancel}
              variant="contained">
              {t("users.cancel")}
            </ConfirmationDialog.Button>
            <ConfirmationDialog.Button
              isLoading={deleteUserMutation.isPending}
              loadingText={t("users.deleting")}
              onClick={handleDeleteConfirm}
              variant="secondary">
              {t("users.delete")}
            </ConfirmationDialog.Button>
          </ConfirmationDialog.Actions>
        </ConfirmationDialog.Root>
      </>
    );
  };

  return (
    <PageLayout isLoading={isUserLoading}>{renderPageContent()}</PageLayout>
  );
};
